// ── MonthlyProgressChart — 이달 누적 매출 + 목표 트랙 + 월말 예측 ──
// SVG 라인 차트. 가계부 엑셀의 "누적 차트" 패턴 차용
//
// 표시:
//   - 실선 녹색: 일별 누적 매출
//   - 점선 회색: 목표 트랙 (목표 / 총일수 × 경과일)
//   - 오늘 마커
//   - 월말 예측 (현재 페이스 유지 시 예상 매출)
//   - 일평균 매출
import React, { useMemo } from 'react';
import { loadGoals } from '../utils/goals';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const fmtWon = n => fmt(n) + '원';

export default function MonthlyProgressChart({ transactions = [], monthKey, isMobile }) {
  const goals = loadGoals();
  const targetRevenue = Number(goals.revenue) || 0;

  // monthKey → year, month, lastDay
  const [yStr, mStr] = monthKey.split('-');
  const year = parseInt(yStr, 10);
  const month = parseInt(mStr, 10);
  const lastDay = new Date(year, month, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
  const dayOfMonth = isCurrentMonth ? today.getDate() : lastDay;

  // ── 일별 매출 + 누적 ──
  const { dailySeries, cumSeries, todayCum, dailyAvg, eomEstimate, achievementPct } = useMemo(() => {
    // dailyMap[day] = amount
    const dailyMap = {};
    if (Array.isArray(transactions) && monthKey) {
      for (const t of transactions) {
        if (t?.type !== 'income') continue;
        if (!t.date || !t.date.startsWith(monthKey)) continue;
        const day = parseInt(t.date.slice(8, 10), 10);
        if (!day || day < 1 || day > 31) continue;
        const amt = (Number(t.supply) || 0) + (Number(t.vat) || 0);
        dailyMap[day] = (dailyMap[day] || 0) + amt;
      }
    }

    // 일별 / 누적 시리즈 (1~lastDay)
    const daily = [];
    const cum = [];
    let runningSum = 0;
    for (let d = 1; d <= lastDay; d++) {
      const amt = dailyMap[d] || 0;
      daily.push({ day: d, amount: amt });
      runningSum += amt;
      cum.push({ day: d, amount: runningSum });
    }

    const todayCum = cum[dayOfMonth - 1]?.amount || 0;
    // 일평균 (현재까지 입력 일수 기준 — 0인 날도 포함)
    const dailyAvg = dayOfMonth > 0 ? Math.round(todayCum / dayOfMonth) : 0;
    const eomEstimate = Math.round(dailyAvg * lastDay);
    const achievementPct = targetRevenue > 0 ? (todayCum / targetRevenue) * 100 : null;

    return { dailySeries: daily, cumSeries: cum, todayCum, dailyAvg, eomEstimate, achievementPct };
  }, [transactions, monthKey, lastDay, dayOfMonth, targetRevenue]);

  // ── SVG 차트 ──
  const W = 600;       // viewBox width
  const H = 220;       // viewBox height
  const PADDING = { top: 18, right: 16, bottom: 28, left: 56 };
  const innerW = W - PADDING.left - PADDING.right;
  const innerH = H - PADDING.top - PADDING.bottom;

  // Y축 max
  const maxCum = Math.max(...cumSeries.map(p => p.amount), targetRevenue, 1);
  const yMax = maxCum * 1.05;
  // X축: day → x
  const xFor = (day) => PADDING.left + ((day - 1) / Math.max(1, lastDay - 1)) * innerW;
  const yFor = (amount) => PADDING.top + innerH - (amount / yMax) * innerH;

  // 누적 라인 (오늘까지만 그림)
  const cumPath = cumSeries.slice(0, dayOfMonth).map((p, i) =>
    `${i === 0 ? 'M' : 'L'} ${xFor(p.day)} ${yFor(p.amount)}`
  ).join(' ');

  // 목표 트랙 라인 (1일~말일까지 균등 증가)
  const targetTrackPath = targetRevenue > 0
    ? `M ${xFor(1)} ${yFor(targetRevenue / lastDay * 1)} L ${xFor(lastDay)} ${yFor(targetRevenue)}`
    : null;

  // Y축 격자/라벨 — 5단계
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => yMax * t);

  // X축 라벨 — 매 7일 단위
  const xTicks = [1, 7, 14, 21, 28, lastDay].filter((v, i, a) => a.indexOf(v) === i && v <= lastDay);

  // 오늘 마커
  const todayPoint = cumSeries[dayOfMonth - 1];

  // 색상 — 진행 상황에 따라
  const onTrackPct = targetRevenue > 0 ? (todayCum / (targetRevenue * dayOfMonth / lastDay)) : null;  // 이 시점 대비 100% = 트랙
  const lineColor = onTrackPct === null ? '#FF6F0F'
    : onTrackPct >= 1 ? '#FF6F0F'
    : onTrackPct >= 0.85 ? '#C2410C'
    : onTrackPct >= 0.6 ? '#EA580C'
    : '#52525B';

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: isMobile ? '16px 14px' : '18px 22px',
      marginBottom: 18,
      boxShadow: '0 3px 18px rgba(0,0,0,0.08)',
    }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a' }}>
          📈 이달 진행 차트
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
          누적 매출 + 목표 트랙 + 월말 예측
        </div>
      </div>

      {/* KPI 4개 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: 8, marginBottom: 14,
      }}>
        <Kpi label="현재 누적" value={fmtWon(todayCum)} color={lineColor} bigger />
        <Kpi label="일평균" value={fmtWon(dailyAvg)} color="#374151" />
        <Kpi label="월말 예측" value={fmtWon(eomEstimate)} color="#1A56A0" />
        <Kpi
          label={targetRevenue > 0 ? '목표 달성률' : '목표 미설정'}
          value={achievementPct !== null ? `${achievementPct.toFixed(0)}%` : '—'}
          color={lineColor}
        />
      </div>

      {/* SVG 차트 */}
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto', maxHeight: 260 }}>

          {/* 격자 + Y축 라벨 */}
          {yTicks.map((tv, i) => (
            <g key={i}>
              <line
                x1={PADDING.left} y1={yFor(tv)}
                x2={W - PADDING.right} y2={yFor(tv)}
                stroke="#f3f4f6" strokeWidth="1"
              />
              <text
                x={PADDING.left - 6} y={yFor(tv) + 4}
                fontSize="10" fill="#9ca3af" textAnchor="end"
              >
                {tv >= 1000000 ? `${(tv / 1000000).toFixed(1)}M`
                  : tv >= 10000 ? `${Math.round(tv / 10000)}만`
                  : Math.round(tv).toLocaleString('ko-KR')}
              </text>
            </g>
          ))}

          {/* X축 라벨 */}
          {xTicks.map(d => (
            <text key={d}
              x={xFor(d)} y={H - PADDING.bottom + 14}
              fontSize="10" fill="#9ca3af" textAnchor="middle"
            >{d}일</text>
          ))}

          {/* 목표 트랙 (점선) */}
          {targetTrackPath && (
            <path d={targetTrackPath}
              fill="none" stroke="#9ca3af" strokeWidth="1.5"
              strokeDasharray="4 4" />
          )}

          {/* 누적 매출 라인 */}
          <path d={cumPath}
            fill="none" stroke={lineColor} strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" />

          {/* 영역 채우기 (라인 아래) */}
          {cumSeries.length > 0 && cumPath && (
            <path
              d={`${cumPath} L ${xFor(dayOfMonth)} ${yFor(0)} L ${xFor(1)} ${yFor(0)} Z`}
              fill={lineColor} opacity="0.08"
            />
          )}

          {/* 오늘 마커 */}
          {todayPoint && isCurrentMonth && (
            <>
              <circle
                cx={xFor(todayPoint.day)} cy={yFor(todayPoint.amount)}
                r="5" fill={lineColor} stroke="#fff" strokeWidth="2"
              />
              <text
                x={xFor(todayPoint.day)} y={yFor(todayPoint.amount) - 12}
                fontSize="10" fill={lineColor} fontWeight="800" textAnchor="middle"
              >오늘</text>
            </>
          )}

          {/* 목표 끝점 마커 */}
          {targetRevenue > 0 && (
            <>
              <circle
                cx={xFor(lastDay)} cy={yFor(targetRevenue)}
                r="4" fill="#9ca3af" stroke="#fff" strokeWidth="1.5"
              />
              <text
                x={xFor(lastDay) - 4} y={yFor(targetRevenue) - 8}
                fontSize="9" fill="#6b7280" fontWeight="700" textAnchor="end"
              >🎯 목표</text>
            </>
          )}
        </svg>
      </div>

      {/* 범례 */}
      <div style={{
        display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'center',
        marginTop: 8, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#6b7280' }}>
          <div style={{ width: 18, height: 2, background: lineColor, borderRadius: 1 }} />
          누적 매출
        </div>
        {targetRevenue > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#6b7280' }}>
            <div style={{ width: 18, height: 0, borderTop: '2px dashed #9ca3af' }} />
            목표 트랙
          </div>
        )}
      </div>

      {/* 진단 메시지 */}
      {targetRevenue > 0 && onTrackPct !== null && (
        <div style={{
          marginTop: 10, padding: '8px 12px',
          background: onTrackPct >= 1 ? '#FAFAFA'
            : onTrackPct >= 0.85 ? '#FAFAFA'
            : onTrackPct >= 0.6 ? '#F4F4F5'
            : '#fef2f2',
          borderRadius: 8, fontSize: 11, lineHeight: 1.6,
          color: onTrackPct >= 1 ? '#9A3412'
            : onTrackPct >= 0.85 ? '#854d0e'
            : onTrackPct >= 0.6 ? '#52525B'
            : '#52525B',
        }}>
          {onTrackPct >= 1
            ? `🎯 목표 페이스를 ${((onTrackPct - 1) * 100).toFixed(0)}% 초과 달성 중. 이대로면 월말 ${fmtWon(eomEstimate)} 예상`
            : onTrackPct >= 0.85
            ? `👍 목표 트랙에 거의 근접. 약간만 더 끌어올리면 달성. 월말 예상 ${fmtWon(eomEstimate)}`
            : onTrackPct >= 0.6
            ? `⚠️ 목표 페이스 대비 ${((1 - onTrackPct) * 100).toFixed(0)}% 부족. 마케팅·이벤트 검토 권장`
            : `🔴 목표 페이스 크게 미달 (${(onTrackPct * 100).toFixed(0)}%). 월말 예상 ${fmtWon(eomEstimate)} — 즉시 조치 필요`}
        </div>
      )}

      {!targetRevenue && (
        <div style={{
          marginTop: 10, padding: '8px 12px',
          background: '#f0f9ff', borderRadius: 8,
          fontSize: 11, color: '#0369a1', lineHeight: 1.6,
        }}>
          💡 매출 목표를 설정하면 목표 트랙과 달성률이 표시돼요. 위쪽 "🎯 매장 목표 관리"에서 설정 가능
        </div>
      )}
    </div>
  );
}

// ── 작은 KPI 카드 ──
function Kpi({ label, value, color, bigger }) {
  return (
    <div style={{
      background: '#fafbfc',
      borderRadius: 8, padding: '8px 10px',
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: bigger ? 14 : 13, fontWeight: 900, color, lineHeight: 1.2,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{value}</div>
    </div>
  );
}
